import React, { useState } from "react";
import PageLayout from "@/pages/settings/components/PageLayout";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import Modal from "@/components/controls/Modal";
import { useMyPat, useCreatePat, useDeletePat } from "@/entities/pat/hooks";
import { TokenCreateResponse } from "@/entities/pat/types";
import { Copy, Check, AlertTriangle } from "lucide-react";
import { SectionProps } from "@/pages/settings/components/sections/Section";
import { useTranslation } from "react-i18next";

const EXPIRATION_OPTIONS = [
  { id: "30", displayName: "30 days" },
  { id: "60", displayName: "60 days" },
  { id: "90", displayName: "90 days" },
  { id: "180", displayName: "180 days" },
  { id: "365", displayName: "1 year" }
];

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "Never";
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const TRANSLATION_KEY = "settings.apiKeys";

export const PersonalApiKey: React.FC = () => {
  const { t } = useTranslation();
  const { data: patStatus, isLoading } = useMyPat();
  const createPat = useCreatePat();
  const deletePat = useDeletePat();

  const [expiresInDays, setExpiresInDays] = useState("90");
  const [createdToken, setCreatedToken] = useState<TokenCreateResponse | null>(
    null
  );
  const [copied, setCopied] = useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);

  const hasToken = patStatus?.hasToken && patStatus.token;

  const handleCreate = () => {
    createPat.mutate(
      { name: "webapp-pat", expiresInDays: parseInt(expiresInDays) },
      {
        onSuccess: (response: TokenCreateResponse) => {
          setCreatedToken(response);
        }
      }
    );
  };

  const handleReplace = () => {
    setShowReplaceDialog(false);
    handleCreate();
  };

  const handleRevoke = () => {
    if (!patStatus?.token) return;
    deletePat.mutate(
      { pathParams: { tokenId: patStatus.token.id } },
      {
        onSuccess: () => {
          setCreatedToken(null);
          setShowRevokeDialog(false);
        }
      }
    );
  };

  const handleCopy = async () => {
    if (!createdToken?.token) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable in non-secure contexts
    }
  };

  const handleGenerateClick = () => {
    if (hasToken) {
      setShowReplaceDialog(true);
    } else {
      handleCreate();
    }
  };

  const sections: SectionProps[] = [];

  // Token metadata section
  if (hasToken && patStatus.token) {
    sections.push({
      title: t(`${TRANSLATION_KEY}.activeKey`),
      items: [
        {
          title: t(`${TRANSLATION_KEY}.created`),
          textValue: formatDate(patStatus.token.createdAt)
        },
        {
          title: t(`${TRANSLATION_KEY}.expires`),
          textValue: formatDate(patStatus.token.expiresAt)
        },
        {
          title: t(`${TRANSLATION_KEY}.lastUsed`),
          textValue: formatDate(patStatus.token.lastUsedAt)
        }
      ] as SettingItemLayoutProps[]
    });
  }

  // Actions section
  const actionItems: SettingItemLayoutProps[] = [
    {
      title: "Expiration",
      actionType: "singleSelect",
      actionProps: {
        fieldName: "expiresInDays",
        options: EXPIRATION_OPTIONS,
        selectedValue: expiresInDays,
        onSelection: (value: string | number) =>
          setExpiresInDays(value as string)
      }
    },
    {
      title: hasToken ? "Regenerate" : "Generate",
      subTitle: hasToken
        ? "Create a new key and revoke the current one"
        : "Create a new personal access token",
      customAction: (
        <Button
          onClick={handleGenerateClick}
          disabled={createPat.isPending}
          data-test="pat-generate-button"
        >
          {hasToken ? "Regenerate Key" : "Generate Key"}
        </Button>
      )
    }
  ];

  if (hasToken) {
    actionItems.push({
      title: "Revoke",
      subTitle: "Permanently revoke your current API key",
      customAction: (
        <Button
          variant="secondary"
          destructive
          onClick={() => setShowRevokeDialog(true)}
          disabled={deletePat.isPending}
          data-test="pat-revoke-button"
        >
          Revoke Key
        </Button>
      )
    });
  }

  sections.push({ items: actionItems });

  return (
    <>
      <PageLayout
        data-test="settings-api-keys-page"
        title={t(`${TRANSLATION_KEY}.title`)}
        subTitle={t(`${TRANSLATION_KEY}.subtitle`)}
        sections={isLoading ? undefined : sections}
      >
        {/* Token value banner — shown once after creation, above the sections */}
        {createdToken && (
          <div className="flex flex-col gap-3 rounded-lg border border-warning bg-surface-warning-subtle p-4 w-full">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <Text variant="smSubtitle">
                Copy your API key now. It won't be shown again.
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={createdToken.token}
                containerClassName="flex-1"
                className="font-mono text-sm"
                data-test="pat-token-value"
              />
              <Button
                variant="secondary"
                onClick={handleCopy}
                data-test="pat-copy-button"
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        )}
      </PageLayout>

      {/* Replace confirmation */}
      <Modal
        open={showReplaceDialog}
        setIsOpen={setShowReplaceDialog}
        contentClassname="w-[35rem]"
        title={
          <Text as="div" variant="lgSubtitleH1">
            Regenerate API Key?
          </Text>
        }
        content={
          <div className="flex flex-col gap-3 p-4">
            <Text variant="body">
              Your existing API key will be revoked immediately. Any
              integrations using it will stop working.
            </Text>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondaryNoBg"
              onClick={() => setShowReplaceDialog(false)}
            >
              Cancel
            </Button>
            <Button destructive onClick={handleReplace}>
              Regenerate Key
            </Button>
          </div>
        }
        hideDivider
        data-test="pat-replace-dialog"
      />

      {/* Revoke confirmation */}
      <Modal
        open={showRevokeDialog}
        setIsOpen={setShowRevokeDialog}
        contentClassname="w-[35rem]"
        title={
          <Text as="div" variant="lgSubtitleH1">
            Revoke API Key?
          </Text>
        }
        content={
          <div className="flex flex-col gap-3 p-4">
            <Text variant="body">
              This will permanently revoke your API key. Any integrations using
              it will stop working immediately.
            </Text>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondaryNoBg"
              onClick={() => setShowRevokeDialog(false)}
            >
              Cancel
            </Button>
            <Button destructive onClick={handleRevoke}>
              Revoke Key
            </Button>
          </div>
        }
        hideDivider
        data-test="pat-revoke-dialog"
      />
    </>
  );
};
